import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import inventoryService, { Station, Product } from '../../services/inventoryService';

interface StationProductPair {
  stationId: number;
  stationName: string;
  productSku: string;
  currentProduct?: string;
}

interface BulkProductAssignmentProps {
  onClose: () => void;
  onSuccess: () => void;
}

const BulkProductAssignment: React.FC<BulkProductAssignmentProps> = ({ 
  onClose, 
  onSuccess 
}) => {
  const queryClient = useQueryClient();
  const [assignments, setAssignments] = useState<StationProductPair[]>([]);
  const [locationFilter, setLocationFilter] = useState('');

  // Fetch stations
  const { data: stations, isLoading: stationsLoading } = useQuery(
    ['stations-bulk-assignment'],
    () => inventoryService.getStations({})
  );

  // Fetch products
  const { data: productsData, isLoading: productsLoading } = useQuery(
    ['products-bulk-assignment'],
    () => inventoryService.getProducts({ limit: 1000 })
  );

  const products = productsData?.products || [];

  // Filter stations by location
  const filteredStations = React.useMemo(() => {
    if (!stations) return [];
    if (!locationFilter) return stations;
    return stations.filter((s: Station) => s.location_code === locationFilter);
  }, [stations, locationFilter]);

  // Get unique locations
  const locations = React.useMemo(() => {
    if (!stations) return [];
    const uniqueLocations = new Set(
      stations.map((s: Station) => s.location_code).filter(Boolean)
    );
    return Array.from(uniqueLocations).sort();
  }, [stations]);

  // Initialize assignments when stations load
  React.useEffect(() => {
    if (filteredStations.length > 0 && assignments.length === 0) {
      const initialAssignments = filteredStations.map((station: Station) => ({
        stationId: station.station_id,
        stationName: station.station_name,
        productSku: station.assigned_product_sku || '',
        currentProduct: station.assigned_product_name || undefined,
      }));
      setAssignments(initialAssignments);
    }
  }, [filteredStations]);

  // Update assignment
  const handleProductChange = (stationId: number, productSku: string) => {
    setAssignments(prev => 
      prev.map(a => 
        a.stationId === stationId 
          ? { ...a, productSku } 
          : a
      )
    );
  };

  // Bulk update mutation
  const bulkUpdateMutation = useMutation(
    async (assignments: StationProductPair[]) => {
      const promises = assignments
        .filter(a => a.productSku !== '') // Only update stations with products
        .map(a => 
          inventoryService.updateStation(a.stationId, {
            assigned_product_sku: a.productSku || undefined,
          })
        );
      return Promise.all(promises);
    },
    {
      onSuccess: (results) => {
        toast.success(`Successfully assigned products to ${results.length} stations`);
        queryClient.invalidateQueries('stations');
        onSuccess();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update assignments');
      },
    }
  );

  const handleSaveAll = () => {
    if (assignments.filter(a => a.productSku).length === 0) {
      toast.error('Please assign at least one product');
      return;
    }
    bulkUpdateMutation.mutate(assignments);
  };

  const handleClearAll = () => {
    setAssignments(prev => 
      prev.map(a => ({ ...a, productSku: '' }))
    );
  };

  const changedCount = assignments.filter((a, idx) => {
    const station = filteredStations[idx];
    return station && a.productSku !== (station.assigned_product_sku || '');
  }).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Modal Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Bulk Product Assignment</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Assign products to multiple stations at once for production planning
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={bulkUpdateMutation.isLoading}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Location Filter */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filter by Location
            </label>
            <select
              value={locationFilter}
              onChange={(e) => {
                setLocationFilter(e.target.value);
                setAssignments([]); // Reset assignments when location changes
              }}
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Locations</option>
              {locations.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          </div>

          {/* Summary */}
          <div className="mb-6 grid grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm text-blue-600 font-medium">Total Stations</div>
              <div className="text-2xl font-bold text-blue-900">{filteredStations.length}</div>
            </div>
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-sm text-green-600 font-medium">With Products</div>
              <div className="text-2xl font-bold text-green-900">
                {assignments.filter(a => a.productSku).length}
              </div>
            </div>
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="text-sm text-yellow-600 font-medium">Pending Changes</div>
              <div className="text-2xl font-bold text-yellow-900">{changedCount}</div>
            </div>
          </div>

          {/* Assignments Table */}
          <div className="mb-6">
            {stationsLoading || productsLoading ? (
              <div className="text-center py-8 text-gray-500">Loading data...</div>
            ) : filteredStations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No stations found</div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Station
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assign Product
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {assignments.map((assignment, index) => {
                      const station = filteredStations.find((s: Station) => s.station_id === assignment.stationId);
                      const isChanged = station && assignment.productSku !== (station.assigned_product_sku || '');
                      
                      return (
                        <tr key={assignment.stationId} className={isChanged ? 'bg-yellow-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900 dark:text-white">{assignment.stationName}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">ID: {assignment.stationId}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {station?.location_code}
                          </td>
                          <td className="px-6 py-4">
                            {assignment.currentProduct ? (
                              <div className="text-sm">
                                <div className="font-medium text-gray-900 dark:text-white">{assignment.currentProduct}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{station?.assigned_product_sku}</div>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic text-sm">Not assigned</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <select
                              value={assignment.productSku}
                              onChange={(e) => handleProductChange(assignment.stationId, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                              disabled={bulkUpdateMutation.isLoading}
                            >
                              <option value="">No product</option>
                              {products.map((product: Product) => (
                                <option key={product.sku} value={product.sku}>
                                  {product.name} ({product.sku})
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {isChanged && (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                Modified
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Actions Info */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-blue-800">
                <div className="font-medium mb-1">Bulk Assignment Tips</div>
                <ul className="list-disc list-inside space-y-1">
                  <li>Select products from the dropdown for each station</li>
                  <li>Rows highlighted in yellow indicate pending changes</li>
                  <li>Use "Clear All" to remove all product assignments</li>
                  <li>Only stations with assigned products will be updated</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-between pt-4 border-t">
            <button
              type="button"
              onClick={handleClearAll}
              className="px-4 py-2 bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200 rounded-md hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
              disabled={bulkUpdateMutation.isLoading}
            >
              Clear All
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 transition-colors"
                disabled={bulkUpdateMutation.isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAll}
                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={changedCount === 0 || bulkUpdateMutation.isLoading}
              >
                {bulkUpdateMutation.isLoading 
                  ? 'Saving...' 
                  : `Save ${changedCount} Change${changedCount !== 1 ? 's' : ''}`
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkProductAssignment;
